import { desktop } from './desktop';
import { detectPlatform } from './media';
import { createStore, useStore } from './store';

// Where a finished recording goes. It always stays on the device that recorded it.
//  · Computers (desktop app and browsers): straight to the Downloads folder.
//  · Phones and tablets: iOS does not let a page (least of all one added to the home screen)
//    download a file by itself, so the recording waits until the person taps «Guardar», which
//    opens the system share sheet: «Guardar video» (Photos), «Guardar en Archivos», WhatsApp…
//    Recordings waiting to be saved survive leaving the meeting; closing the page warns first.

export interface PendingRecording {
  id: number;
  file: File;
}

export const pendingRecordings = createStore<{ items: PendingRecording[] }>({ items: [] });
export const usePendingRecordings = () => useStore(pendingRecordings, (s) => s.items);

let nextId = 1;

function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** Phones, tablets and home-screen apps save through a button; computers download directly. */
export function savesWithButton(): boolean {
  if (desktop) return false;
  return detectPlatform() !== 'desktop' || isStandalone();
}

export function downloadFile(file: File): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(file);
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
}

export function sizeLabel(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

/** Where the person will find the file, in their words. */
export function downloadedWhere(): string {
  if (desktop) return 'en la carpeta Descargas';
  return 'en la carpeta de descargas del navegador (normalmente «Descargas»)';
}

export function keepForSaving(file: File): void {
  pendingRecordings.set({ items: [...pendingRecordings.get().items, { id: nextId++, file }] });
}

export function forget(id: number): void {
  pendingRecordings.set({ items: pendingRecordings.get().items.filter((r) => r.id !== id) });
}

/**
 * Must run from a tap (user gesture). Returns true once the file left the page (saved or sent),
 * false if the person closed the share sheet.
 */
export async function saveFromButton(item: PendingRecording): Promise<boolean> {
  const share = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
  if (share.share && share.canShare?.({ files: [item.file] })) {
    try {
      await share.share({ files: [item.file], title: item.file.name });
      forget(item.id);
      return true;
    } catch (err) {
      if ((err as DOMException).name === 'AbortError') return false;
      /* sharing failed: fall back to a download */
    }
  }
  downloadFile(item.file);
  forget(item.id);
  return true;
}

// Closing or reloading the page with recordings still waiting asks first.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (pendingRecordings.get().items.length) {
      e.preventDefault();
      e.returnValue = '';
    }
  });
}
